import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    Alert,
    CircularProgress,
    Divider,
} from "@mui/material";
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import axios from "axios";
import "./AppointmentBookingModal.scss";

const AppointmentBookingModal = ({
    open,
    onClose,
    doctor,
    slot,
    job_id,
    patient,
    orgCode,
    appMode
}) => {
    const [isBooking, setIsBooking] = useState(false);
    const [notes, setNotes] = useState("");
    const [bookingSuccess, setBookingSuccess] = useState(false);
    const [bookingDetails, setBookingDetails] = useState(null);

    const REACT_APP_BACKEND_ROUTE_URL =
        process.env.REACT_APP_BACKEND_ROUTE_URL;

    const { patientId, patientUsername, patientPassword } = patient;

    const handleBookAppointment = async () => {
        if (!patientId) {
            toast.error("Patient ID is required");
            return;
        }

        setIsBooking(true);
        try {
            // Parse slot times
            const slotDate = doctor.next_available_date;
            const startTime = convertToDateTime(slotDate, slot.slot_start_time);
            const endTime = convertToDateTime(slotDate, slot.slot_end_time);

            const appointmentData = {
                "job_id": job_id,
                "doctor_id": doctor.doctor_id,
                "appointment_start_time": startTime,
                "appointment_end_time": endTime,
                "appointment_mode": "OFFLINE",
                "status": "SCHEDULED",
                "shift": slot.shiftNumber,
                "slot_number": slot.slot_number,
                "username": patientUsername,
                "password": patientPassword
            }

            const response = await axios.post(
                `${REACT_APP_BACKEND_ROUTE_URL}book_patient_appointment/`,
                appointmentData,
                {
                    headers: {
                        "Org-Code": orgCode,
                        "App-Mode": appMode
                    }
                }
            );

            if (response?.data?.status === "Success" || response?.data?.message?.includes("success")) {
                // Store booking details for success screen
                setBookingDetails({
                    date: doctor.next_available_date,
                    startTime: slot.slot_start_time,
                    endTime: slot.slot_end_time,
                    doctorName: doctor.salutation ? `${doctor.salutation} ${doctor.name}` : doctor.name
                });
                setBookingSuccess(true);
            } else {
                toast.error(
                    response?.data?.message || "Failed to book appointment. Please try again."
                );
            }
        } catch (error) {
            console.error("Error booking appointment:", error);
            toast.error(
                error?.response?.data?.message ||
                "Unable to book appointment. Please try again."
            );
        } finally {
            setIsBooking(false);
        }
    };

    const convertToDateTime = (dateStr, time12h, addDays = 0) => {
        if (!dateStr || !time12h) return null;

        // Split time and modifier
        const [time, modifier] = time12h.trim().split(/\s*(AM|PM)/i);
        let [hours, minutes] = time.split(":");

        hours = parseInt(hours, 10);

        if (modifier?.toUpperCase() === "PM" && hours !== 12) {
            hours += 12;
        } else if (modifier?.toUpperCase() === "AM" && hours === 12) {
            hours = 0;
        }

        // Create Date object
        const date = new Date(dateStr);
        date.setDate(date.getDate() + addDays);
        date.setHours(hours);
        date.setMinutes(parseInt(minutes || "00", 10));
        date.setSeconds(0);
        date.setMilliseconds(0);

        // Format: YYYY-MM-DDTHH:mm
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const hh = String(date.getHours()).padStart(2, "0");
        const min = String(date.getMinutes()).padStart(2, "0");

        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    };


    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    return (
        <Dialog
            open={open}
            onClose={bookingSuccess ? undefined : onClose}
            disableEscapeKeyDown={bookingSuccess}
            maxWidth="sm"
            fullWidth
            className="appointment-booking-modal"
        >
            <DialogTitle>
                <Box className="modal-header">
                    <Typography variant="h6" fontWeight="bold">
                        {bookingSuccess ? "Booking Successful" : "Confirm Appointment"}
                    </Typography>
                    {!bookingSuccess && (
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            disabled={isBooking}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent>
                {bookingSuccess && bookingDetails ? (
                    <Box className="appointment-details" sx={{ textAlign: "center", py: 4 }}>
                        <Box sx={{ mb: 3 }}>
                            <CheckCircleIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
                            <Typography variant="h4" fontWeight="bold" color="success.main" gutterBottom>
                                Booking Successful!
                            </Typography>
                        </Box>

                        <Box className="detail-section" sx={{ mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                                Appointment Details
                            </Typography>
                        </Box>

                        <Box className="detail-section" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Date
                            </Typography>
                            <Typography variant="h6" fontWeight="medium">
                                {formatDate(bookingDetails.date)}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box className="detail-section" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Time Slot
                            </Typography>
                            <Typography variant="h6" fontWeight="medium">
                                {bookingDetails.startTime} - {bookingDetails.endTime}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box className="appointment-details">
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Please review the appointment details before confirming.
                        </Alert>

                        <Box className="detail-section">
                            <Typography variant="subtitle2" color="text.secondary">
                                Doctor
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                                {doctor.salutation ? `${doctor.salutation} ` : ""}
                                {doctor.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {doctor.specialization}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box className="detail-section">
                            <Typography variant="subtitle2" color="text.secondary">
                                Date
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                                {formatDate(doctor.next_available_date)}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box className="detail-section">
                            <Typography variant="subtitle2" color="text.secondary">
                                Time Slot
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                                {slot.slot_start_time} - {slot.slot_end_time}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box className="detail-section">
                            <Typography variant="subtitle2" color="text.secondary">
                                Consultation Fee
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                                ₹{doctor.consultation_fees}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Additional Notes (Optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add any additional information..."
                            disabled={isBooking}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                )}
            </DialogContent>
            {!bookingSuccess && (
                <DialogActions className="modal-actions">
                    <Button onClick={onClose} disabled={isBooking}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleBookAppointment}
                        disabled={isBooking}
                        startIcon={isBooking ? <CircularProgress size={20} /> : null}
                    >
                        {isBooking ? "Booking..." : "Confirm Appointment"}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default AppointmentBookingModal;

